import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import type { JSX } from 'react';

import { parseWorkspaceManageSearch } from '@/features/workspace/manage/manage-search.js';
import { WorkspaceManagePage } from '@/features/workspace/manage/WorkspaceManagePage.js';
import { useRouteWorkspaceSync } from '@/features/workspace/useRouteWorkspaceSync.js';

export const Route = createFileRoute('/workspaces/$id/manage')({
  component: WorkspaceManageRoute,
  validateSearch: parseWorkspaceManageSearch,
});

/**
 * `/workspaces/:id/manage` — workspace management page (General / Access / Agent / Knowledge /
 * Connectors / Credentials / Callbacks / Quality / Developer / Code Quality). Shared files live in
 * their own `/artifacts` section.
 *
 * Landing on `/workspaces/:id/manage` (shareable link or in-app navigation) always honours the URL
 * `:id` and syncs the switcher to match. After that, a user-driven sidebar switch while this route
 * stays mounted renders the newly selected workspace immediately and then updates the URL. The URL
 * keeps only the tab: a Code Quality drill-down level names groups of the workspace left behind.
 */
function WorkspaceManageRoute(): JSX.Element {
  const { id } = useParams({ from: '/workspaces/$id/manage' });
  const { tab } = useSearch({ from: '/workspaces/$id/manage' });
  const navigate = useNavigate();
  const activeWorkspaceId = useRouteWorkspaceSync(id, (workspaceId) => {
    void navigate({
      from: '/workspaces/$id/manage',
      to: '/workspaces/$id/manage',
      params: { id: workspaceId },
      search: { tab },
      replace: true,
    });
  });

  return (
    <WorkspaceManagePage
      key={activeWorkspaceId}
      workspaceId={activeWorkspaceId}
    />
  );
}
